import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, Facebook, Linkedin, MapPin } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

const Contact = () => {
  const { language, t } = useLanguage();

  return (
    <>
      <Helmet>
        <title>{language === 'en' ? 'Contact | LeaderProMed Mauritania' : 'Contactez-Nous | LeaderProMed Mauritanie'}</title>
        <meta name="description" content={language === 'en' ? 'Contact LeaderProMed for any information, partnership or medical service request in Mauritania and North Africa.' : 'Contactez LeaderProMed pour toute demande d\'information, partenariat ou service médical en Mauritanie et Afrique du Nord.'} />
        <meta property="og:title" content={language === 'en' ? 'Contact | LeaderProMed Mauritania' : 'Contactez-Nous | LeaderProMed Mauritanie'} />
        <meta property="og:description" content={language === 'en' ? 'Contact LeaderProMed for any information, partnership or medical service request in Mauritania and North Africa.' : 'Contactez LeaderProMed pour toute demande d\'information, partenariat ou service médical en Mauritanie et Afrique du Nord.'} />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-blue-600 mb-2">{t('contact.title')}</h1>
            <div className="h-1 w-20 bg-blue-600 mb-4 mx-auto"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Formulaire de Contact */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-semibold text-blue-600 mb-6">{t('contact.form.title')}</h2>
                <form className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('contact.form.name')}</Label>
                    <Input
                      id="name"
                      placeholder={t('contact.form.namePlaceholder')}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t('contact.form.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('contact.form.emailPlaceholder')}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">{t('contact.form.subject')}</Label>
                    <Input
                      id="subject"
                      placeholder={t('contact.form.subjectPlaceholder')}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t('contact.form.message')}</Label>
                    <Textarea
                      id="message"
                      placeholder={t('contact.form.messagePlaceholder')}
                      required
                      className="min-h-[150px]"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                    {t('contact.form.submit')}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Informations de Contact */}
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-semibold text-blue-600 mb-6">{t('contact.info.title')}</h2>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                        <MapPin size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{t('contact.info.address')}</h3>
                        <p className="text-gray-600">{t('contact.info.addressDetails')}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                        <Mail size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{t('contact.info.email')}</h3>
                        <p className="text-gray-600">{t('contact.info.emailDetails')}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                        <Phone size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{t('contact.info.phone')}</h3>
                        <p className="text-gray-600">{t('contact.info.phoneDetails')}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="font-semibold text-gray-800 mb-4">{t('contact.info.follow')}</h3>
                    <div className="flex gap-4">
                      <a 
                        href="https://www.facebook.com/profile.php?id=100086261242323" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-blue-100 p-3 rounded-full text-blue-600 hover:bg-blue-200 transition-colors"
                      >
                        <Facebook size={24} />
                      </a>
                      <a 
                        href="https://www.linkedin.com/company/leaderpromed" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-blue-100 p-3 rounded-full text-blue-600 hover:bg-blue-200 transition-colors"
                      >
                        <Linkedin size={24} />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Carte Google Maps */}
              <Card>
                <CardContent className="p-0">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3840.0!2d-15.9!3d18.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTjCsDA2JzAwLjAiTiAxNcKwNTQnMDAuMCJX!5e0!3m2!1sfr!2smr!4v1234567890"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localisation de Leader Promed"
                  ></iframe>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Contact;
